/**
 * act-as-cdn-rules.ts — React Query hooks for CDN rules + delivery surfaces via act-as.
 *
 * Covers: header-rules, error-pages, redirects, purge/warm, TLS, and certificates.
 * All mutations use actAsClient so X-Act-As-Org is injected from the active session.
 *
 * Query key factory: actAsCDNRulesKeys.*
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { actAsClient } from '@/api/act-as-client';

/* ── Types: Header Rules ─────────────────────────────────────────────────── */

export interface HeaderRule {
  id?: string;
  phase: 'request' | 'response';
  action: 'set' | 'append' | 'remove';
  header_name: string;
  header_value?: string;
  condition?: string;
  priority: number;
}

export interface HeaderRulesConfig {
  rules: HeaderRule[];
}

/* ── Types: Error Pages ──────────────────────────────────────────────────── */

export interface ErrorPagesConfig {
  enabled: boolean;
  /** Map of HTTP status code string → custom HTML body. */
  pages: Record<string, string>;
}

/* ── Types: Redirects ────────────────────────────────────────────────────── */

export interface RedirectCondition {
  field: string;
  operator: string;
  values: string[];
}

export interface RedirectRule {
  id?: string;
  name: string;
  priority: number;
  destination_url: string;
  status_code: 301 | 302 | 307 | 308;
  conditions: RedirectCondition[];
}

export interface RedirectsConfig {
  rules: RedirectRule[];
}

/* ── Types: Purge ────────────────────────────────────────────────────────── */

export type PurgeMode = 'url' | 'prefix' | 'suffix' | 'tag' | 'all';

export interface PurgeRequest {
  mode: PurgeMode;
  values?: string[];
}

export interface PurgeResponse {
  purge_id?: string;
  status?: string;
}

export interface PurgeStatus {
  purge_id?: string;
  status?: string;
  acks?: string[];
  total_nodes?: number;
  completed_at?: string;
}

export interface WarmResponse {
  node_count?: number;
}

/* ── Types: Warm ─────────────────────────────────────────────────────────── */

export interface WarmRequest {
  urls: string[];
}

/* ── Types: TLS ──────────────────────────────────────────────────────────── */

export interface TlsConfig {
  min_version?: '1.2' | '1.3';
  http2?: boolean;
  http3?: boolean;
  auto_https?: boolean;
  hsts_enabled?: boolean;
  hsts_max_age?: number;
  hsts_subdomains?: boolean;
  hsts_preload?: boolean;
}

/* ── Types: Certificates ─────────────────────────────────────────────────── */

export type CertType = 'uploaded' | 'acme' | 'tiger';

export interface Certificate {
  id: string;
  type: CertType;
  common_name?: string;
  san?: string[];
  issuer?: string;
  expires_at?: string;
  active?: boolean;
  created_at?: string;
}

export interface CertUploadRequest {
  cert_pem: string;
  key_pem: string;
  chain_pem?: string;
}

export interface AcmeRequest {
  email?: string;
}

export interface TigerRequest {
  cert_id?: string;
}

/* ── Query key factory ───────────────────────────────────────────────────── */

export const actAsCDNRulesKeys = {
  headerRules: (domainId: string) =>
    ['act-as', 'cdn', 'domain', domainId, 'header-rules'] as const,
  errorPages: (domainId: string) =>
    ['act-as', 'cdn', 'domain', domainId, 'error-pages'] as const,
  redirects: (domainId: string) =>
    ['act-as', 'cdn', 'domain', domainId, 'redirects'] as const,
  purgeStatus: (domainId: string, purgeId: string) =>
    ['act-as', 'cdn', 'domain', domainId, 'purge', purgeId] as const,
  tls: (domainId: string) =>
    ['act-as', 'cdn', 'domain', domainId, 'tls'] as const,
  certs: (domainId: string) =>
    ['act-as', 'cdn', 'domain', domainId, 'certs'] as const,
};

/* ── Envelope unwrap helper ──────────────────────────────────────────────── */

function unwrap<T>(body: unknown): T | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  if ('data' in b && b.data !== null && b.data !== undefined) return b.data as T;
  return b as unknown as T;
}

/* ── Header Rules hooks ──────────────────────────────────────────────────── */

/**
 * GET /api/v3/cdn/domains/:id/header-rules
 */
export function useActAsCDNHeaderRules(domainId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: actAsCDNRulesKeys.headerRules(domainId ?? ''),
    enabled: Boolean(domainId) && enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<HeaderRulesConfig | null> => {
      try {
        const res = await actAsClient.get<unknown>(`/cdn/domains/${domainId}/header-rules`);
        return unwrap<HeaderRulesConfig>(res.data);
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 400) return null;
        throw e;
      }
    },
  });
}

/* ── Error Pages hooks ───────────────────────────────────────────────────── */

/**
 * GET /api/v3/cdn/domains/:id/error-pages
 */
export function useActAsCDNErrorPages(domainId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: actAsCDNRulesKeys.errorPages(domainId ?? ''),
    enabled: Boolean(domainId) && enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<ErrorPagesConfig | null> => {
      try {
        const res = await actAsClient.get<unknown>(`/cdn/domains/${domainId}/error-pages`);
        return unwrap<ErrorPagesConfig>(res.data);
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 400) return null;
        throw e;
      }
    },
  });
}

/* ── Redirects hooks ─────────────────────────────────────────────────────── */

/**
 * GET /api/v3/cdn/domains/:id/redirects
 */
export function useActAsCDNRedirects(domainId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: actAsCDNRulesKeys.redirects(domainId ?? ''),
    enabled: Boolean(domainId) && enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<RedirectsConfig | null> => {
      try {
        const res = await actAsClient.get<unknown>(`/cdn/domains/${domainId}/redirects`);
        return unwrap<RedirectsConfig>(res.data);
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 400) return null;
        throw e;
      }
    },
  });
}

/* ── Purge Status hook ───────────────────────────────────────────────────── */

/**
 * GET /api/v3/cdn/domains/:id/purge/:purgeId — poll purge status.
 * enabled=false by default; caller enables after submitting a purge job.
 */
export function useActAsCDNPurgeStatus(
  domainId: string,
  purgeId: string | null,
  enabled = false,
) {
  return useQuery({
    queryKey: actAsCDNRulesKeys.purgeStatus(domainId, purgeId ?? ''),
    enabled: Boolean(domainId) && Boolean(purgeId) && enabled,
    refetchInterval: (query) => {
      // Stop polling once all nodes have acked or status reaches a terminal state.
      const d = query.state.data as PurgeStatus | null;
      if (!d) return 3_000;
      const terminal = d.status === 'completed' || d.status === 'failed';
      const allAcked = (d.acks?.length ?? 0) >= (d.total_nodes ?? Infinity);
      return terminal || allAcked ? false : 3_000;
    },
    queryFn: async (): Promise<PurgeStatus | null> => {
      const res = await actAsClient.get<unknown>(
        `/cdn/domains/${domainId}/purge/${purgeId}`,
      );
      return unwrap<PurgeStatus>(res.data);
    },
  });
}

/* ── TLS hooks ───────────────────────────────────────────────────────────── */

/**
 * GET /api/v3/cdn/domains/:id/tls
 */
export function useActAsCDNTls(domainId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: actAsCDNRulesKeys.tls(domainId ?? ''),
    enabled: Boolean(domainId) && enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<TlsConfig | null> => {
      try {
        const res = await actAsClient.get<unknown>(`/cdn/domains/${domainId}/tls`);
        return unwrap<TlsConfig>(res.data);
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 400) return null;
        throw e;
      }
    },
  });
}

/* ── Certificate hooks ───────────────────────────────────────────────────── */

/**
 * GET /api/v3/cdn/domains/:id/certs — list all certificates.
 */
export function useActAsCDNCerts(domainId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: actAsCDNRulesKeys.certs(domainId ?? ''),
    enabled: Boolean(domainId) && enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<Certificate[]> => {
      try {
        const res = await actAsClient.get<unknown>(`/cdn/domains/${domainId}/certs`);
        const unwrapped = unwrap<Certificate[] | { certs?: Certificate[] }>(res.data);
        if (Array.isArray(unwrapped)) return unwrapped;
        return (unwrapped as { certs?: Certificate[] })?.certs ?? [];
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 400) return [];
        throw e;
      }
    },
  });
}

/* ── Header Rule mutations ───────────────────────────────────────────────── */

/** POST / PUT /api/v3/cdn/domains/:id/header-rules[/:id] — create or update. */
export function useSaveActAsCDNHeaderRule(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rule: HeaderRule) => {
      if (rule.id) {
        await actAsClient.put(`/cdn/domains/${domainId}/header-rules/${rule.id}`, rule);
      } else {
        await actAsClient.post(`/cdn/domains/${domainId}/header-rules`, rule);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: actAsCDNRulesKeys.headerRules(domainId) }),
  });
}

/** DELETE /api/v3/cdn/domains/:id/header-rules/:id */
export function useDeleteActAsCDNHeaderRule(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ruleId: string) => {
      await actAsClient.delete(`/cdn/domains/${domainId}/header-rules/${ruleId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: actAsCDNRulesKeys.headerRules(domainId) }),
  });
}

/* ── Error Pages mutations ───────────────────────────────────────────────── */

/** PUT /api/v3/cdn/domains/:id/error-pages */
export function useUpdateActAsCDNErrorPages(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cfg: ErrorPagesConfig) => {
      await actAsClient.put(`/cdn/domains/${domainId}/error-pages`, cfg);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: actAsCDNRulesKeys.errorPages(domainId) }),
  });
}

/** DELETE /api/v3/cdn/domains/:id/error-pages */
export function useDeleteActAsCDNErrorPages(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await actAsClient.delete(`/cdn/domains/${domainId}/error-pages`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: actAsCDNRulesKeys.errorPages(domainId) }),
  });
}

/* ── Redirects mutations ─────────────────────────────────────────────────── */

/** PUT /api/v3/cdn/domains/:id/redirects — replace full rules array. */
export function useUpdateActAsCDNRedirects(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rules: RedirectRule[]) => {
      await actAsClient.put(`/cdn/domains/${domainId}/redirects`, { rules });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: actAsCDNRulesKeys.redirects(domainId) }),
  });
}

/** DELETE /api/v3/cdn/domains/:id/redirects */
export function useDeleteActAsCDNRedirects(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await actAsClient.delete(`/cdn/domains/${domainId}/redirects`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: actAsCDNRulesKeys.redirects(domainId) }),
  });
}

/* ── Purge mutations ─────────────────────────────────────────────────────── */

/** POST /api/v3/cdn/domains/:id/purge */
export function useActAsCDNPurge(domainId: string) {
  return useMutation({
    mutationFn: async (req: PurgeRequest): Promise<PurgeResponse> => {
      const res = await actAsClient.post<unknown>(`/cdn/domains/${domainId}/purge`, req);
      return unwrap<PurgeResponse>(res.data) ?? {};
    },
  });
}

/** POST /api/v3/cdn/domains/:id/warm */
export function useActAsCDNWarm(domainId: string) {
  return useMutation({
    mutationFn: async (req: WarmRequest): Promise<WarmResponse> => {
      const res = await actAsClient.post<unknown>(`/cdn/domains/${domainId}/warm`, req);
      return unwrap<WarmResponse>(res.data) ?? {};
    },
  });
}

/* ── TLS mutations ───────────────────────────────────────────────────────── */

/** PUT /api/v3/cdn/domains/:id/tls */
export function useUpdateActAsCDNTls(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cfg: TlsConfig) => {
      await actAsClient.put(`/cdn/domains/${domainId}/tls`, cfg);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: actAsCDNRulesKeys.tls(domainId) }),
  });
}

/** DELETE /api/v3/cdn/domains/:id/tls */
export function useDeleteActAsCDNTls(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await actAsClient.delete(`/cdn/domains/${domainId}/tls`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: actAsCDNRulesKeys.tls(domainId) }),
  });
}

/* ── Certificate mutations ───────────────────────────────────────────────── */

/** POST /api/v3/cdn/domains/:id/certs — upload PEM bundle. */
export function useUploadActAsCDNCert(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: CertUploadRequest) => {
      await actAsClient.post(`/cdn/domains/${domainId}/certs`, req);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: actAsCDNRulesKeys.certs(domainId) }),
  });
}

/** DELETE /api/v3/cdn/domains/:id/certs/:certId */
export function useDeleteActAsCDNCert(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (certId: string) => {
      await actAsClient.delete(`/cdn/domains/${domainId}/certs/${certId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: actAsCDNRulesKeys.certs(domainId) }),
  });
}

/** POST /api/v3/cdn/domains/:id/certs/:certId/activate */
export function useActivateActAsCDNCert(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (certId: string) => {
      await actAsClient.post(`/cdn/domains/${domainId}/certs/${certId}/activate`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: actAsCDNRulesKeys.certs(domainId) }),
  });
}

/** POST /api/v3/cdn/domains/:id/certs/acme — issue ACME (Let's Encrypt) cert. */
export function useIssueActAsCDNACMECert(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: AcmeRequest = {}) => {
      await actAsClient.post(`/cdn/domains/${domainId}/certs/acme`, req);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: actAsCDNRulesKeys.certs(domainId) }),
  });
}

/** POST /api/v3/cdn/domains/:id/certs/tiger — distribute managed Tiger cert. */
export function useDistributeTigerActAsCDNCert(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: TigerRequest) => {
      await actAsClient.post(`/cdn/domains/${domainId}/certs/tiger`, req);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: actAsCDNRulesKeys.certs(domainId) }),
  });
}
